import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/Logo";
import { toast } from "sonner";
import { Loader2, Mail, Lock } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const redirectTo = location.state?.from?.pathname || "/studio";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success("Welcome back");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403) {
        toast.error("Please verify your email first");
        navigate("/verify-otp", { state: { email } });
      } else {
        toast.error(detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="text-xs uppercase tracking-[0.24em] text-cyan-400 mb-3">Welcome back</div>
      <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">Sign in to AI Bazaar</h1>
      <p className="text-sm text-zinc-400 mt-2">Enter your credentials to continue.</p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input data-testid="login-email" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="h-12 pl-10 bg-white/5 border-white/10 focus-visible:ring-cyan-500/40 focus-visible:border-cyan-500/60" />
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-zinc-500">Password</Label>
            <Link to="/forgot-password" data-testid="forgot-link" className="text-xs text-cyan-400 hover:text-cyan-300">Forgot?</Link>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input data-testid="login-password" id="password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
              className="h-12 pl-10 bg-white/5 border-white/10 focus-visible:ring-cyan-500/40 focus-visible:border-cyan-500/60" />
          </div>
        </div>
        <Button data-testid="login-submit" type="submit" disabled={loading} className="btn-primary w-full h-12 rounded-full font-semibold text-base">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>

      <div className="mt-8 text-sm text-center text-zinc-400">
        New here?{" "}
        <Link to="/signup" data-testid="switch-to-signup" className="text-fuchsia-400 hover:text-fuchsia-300 font-medium">Create an account</Link>
      </div>
    </AuthLayout>
  );
}

export function AuthLayout({ children }) {
  return (
    <div className="relative min-h-screen bg-[#09090B] text-zinc-100 flex items-center justify-center px-5 py-10">
      <div className="ambient-glow -top-40 left-10 w-[380px] h-[380px] opacity-30" style={{ background: "#D946EF" }} />
      <div className="ambient-glow -bottom-40 right-10 w-[380px] h-[380px] opacity-25" style={{ background: "#06B6D4" }} />
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md relative z-10"
      >
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="p-8 rounded-3xl glass-strong">
          {children}
        </div>
      </motion.div>
    </div>
  );
}
